<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCityStore } from '@/store/CityStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import CityList from './CityList.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ManageCityModal from '@/components/city/ManageCityModal.vue';
import ProgressModal from '@/components/modal/ProgressModal.vue';
import ViewDataCityModal from './ViewDataCityModal.vue';
import VersionRevertCity from './VersionRevertCity.vue';

const { t } = useI18n({ useScope: 'global' });

const dialog = useDialog();
const toast = useToast();
const cityStore = useCityStore();

const isLoading = ref(false);
const cityData = ref([]);
const isProgress = ref(false);
const isShowModal = ref(false);
const isSuccess = ref(false);
const isRevert = ref(false);
const urlUpload = computed(() => cityStore.getUrlUploadFile);

const getListCity = async () => {
  isLoading.value = true;
  cityStore
    .getListManagement()
    .then((response: any) => {
      cityData.value = response;
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getListCity();

const openModalManageCity = (city?: any) => {
  const dialogRef = dialog.open(ManageCityModal, {
    props: {
      header: city ? t('common.labels.edit_record') : t('common.labels.add_record'),
      modal: true,
      style: { width: 'min(780px, 80vw)' }
    },
    data: {
      cityData: city
    },
    emits: {
      onApply: () => {
        getListCity();
        dialogRef.close();
      }
    }
  });
};

const handleDeleteCity = (city?: any) => {
  const payload = {
    data: {
      pk: city?.pk,
      sk: city?.sk
    }
  };
  isLoading.value = true;
  cityStore
    .deleteCity(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      getListCity();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handlePreview = ({ city, file, type }: any) => {
  dialog.open(ViewDataCityModal, {
    props: {
      header: t('common.labels.review_upload_file'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      cityList: city,
      type: type
    },
    emits: {
      onApply: () => {
        if (type === 'IMPORT') {
          handleApplyImport(file);
        } else if (type === 'REVERT') {
          handleApplyRevert(file);
        }
      },
      handleReOpen: () => {
        handleShowRevertVersion();
      }
    }
  });
};

const openModalRevert = (versions: any) => {
  dialog.open(VersionRevertCity, {
    props: {
      header: t('common.buttons.revert'),
      style: {
        width: '95vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      versions
    },
    emits: {
      onSelectVersion: (data: any) => {
        handlePreview(data);
      }
    }
  });
};

const handleShowRevertVersion = () => {
  isLoading.value = true;
  cityStore
    .getVersionRevertCity('CITY')
    .then((versions) => {
      openModalRevert(versions);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleApplyImport = (file: any) => {
  isProgress.value = true;
  isShowModal.value = true;
  isRevert.value = false;
  const payload = new FormData();
  payload.append('file', file);
  cityStore
    .importCity(payload)
    .then(() => {
      isSuccess.value = true;
      getListCity();
    })
    .catch(() => {
      isSuccess.value = false;
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const handleApplyRevert = (file: any) => {
  isRevert.value = true;
  isProgress.value = true;
  isShowModal.value = true;
  cityStore
    .revertFileCity(file)
    .then(() => {
      isSuccess.value = true;
      getListCity();
    })
    .catch(() => {
      isSuccess.value = false;
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      isProgress.value = false;
    });
};

const handleCloseModalProgress = (value: boolean) => {
  if (isRevert.value) {
    handleShowRevertVersion();
  }
  isShowModal.value = value;
};
</script>
<template>
  <div class="city-management">
    <LoadingSpinner v-if="isLoading" />
    <div class="card">
      <div class="flex justify-content-between align-items-center">
        <h5 class="city-title">{{ $t('management.gate_des_region_refer') }}</h5>
        <div>
          <Button id="add-new-btn" icon="pi pi-plus" :label="$t('common.labels.add_record_to_table')" class="p-button-primary mb-2" @click="openModalManageCity()" />
        </div>
      </div>
      <CityList
        :city="cityData"
        :urlUpload="urlUpload"
        @handlePreview="(data) => handlePreview(data)"
        @handleShowRevertVersion="handleShowRevertVersion()"
        @handleEditCity="(city) => openModalManageCity(city)"
        @handleDeleteCity="(city) => handleDeleteCity(city)"
      />
    </div>
    <Dialog v-model:visible="isShowModal" :header="$t('management.uploading_gate_des_region_refer')" modal :closable="!isProgress">
      <ProgressModal :isProgress="isProgress" @handleClose="(value) => handleCloseModalProgress(value)" :isSuccess="isSuccess" :isRevert="isRevert" />
    </Dialog>
  </div>
</template>
